import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";

type Props = {
  label: string;
  children: React.ReactNode;
  side?: "top" | "bottom";
  className?: string;
  /** Infobulle courte (icônes toolbar) — une ligne, padding réduit. */
  compact?: boolean;
};

type BubbleCoords = {
  top: number;
  left: number;
};

/** Infobulle stylée — réservée à la barre d’actions du lecteur de message. */
export function MailTooltip({ label, children, side = "top", className, compact = false }: Props) {
  const text = label.trim();
  const tooltipId = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<BubbleCoords>({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const gap = compact ? 5 : 8;
    const vw = window.innerWidth;
    const pad = 10;
    const top = side === "bottom" ? rect.bottom + gap : rect.top - gap;

    let left = rect.left + rect.width / 2;
    // translateX(-50%) : clamper le centre pour que la bulle reste dans le cadre
    const estHalf = compact
      ? Math.min(Math.max(text.length * 3.4, 36), (vw - pad * 2) / 2)
      : Math.min(vw * 0.22, (vw - pad * 2) / 2);
    left = Math.min(Math.max(left, pad + estHalf), vw - pad - estHalf);

    setCoords({ top, left });
  }, [side, compact, text]);

  const show = useCallback(() => {
    setVisible(true);
    requestAnimationFrame(() => updatePosition());
  }, [updatePosition]);

  const hide = useCallback(() => {
    setVisible(false);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    updatePosition();
    const reposition = () => updatePosition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [visible, updatePosition]);

  if (!text) {
    return <>{children}</>;
  }

  return (
    <>
      <span
        ref={anchorRef}
        className={cn("mail-tooltip-root inline-flex max-w-full", className)}
        data-side={side}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={visible ? tooltipId : undefined}
      >
        {children}
      </span>
      {visible
        ? createPortal(
            <span
              id={tooltipId}
              role="tooltip"
              className={cn(
                "mail-tooltip-bubble mail-tooltip-bubble--portal",
                compact && "mail-tooltip-bubble--compact",
                side === "bottom"
                  ? "mail-tooltip-bubble--below"
                  : "mail-tooltip-bubble--above"
              )}
              style={{ top: coords.top, left: coords.left }}
            >
              {text}
            </span>,
            document.body
          )
        : null}
    </>
  );
}
